import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface ProgramImage {
  url: string
  order_num: number
}

interface ProgramSession {
  session_id: number
  session_name: string | null
  run_start: string
  run_end: string
  capacity: number
  order_num: number
}

interface ApplicationSetting {
  allow_member: number
  allow_guest: number
  require_name: number
  require_phone: number
  require_email: number
  require_affiliation: number
  require_grade: number
  consent_policy_id: number | null
}

interface ProgramDetail {
  program_id: number
  title: string | null
  description: string | null
  target: string | null
  location: string | null
  apply_start: string | null
  apply_end: string | null
  run_start: string | null
  run_end: string | null
  schedule_text: string | null
  capacity: number
  has_sessions: boolean
  accepts_online_application: number
  images: ProgramImage[]
  sessions: ProgramSession[]
  is_visible: number
  applied_count: number
  is_closed: number
  application_setting: ApplicationSetting | null
}

// 이미지 슬라이드 — 좌우 화살표 + 하단 점 인디케이터 (이미지 1장이면 화살표/점 숨김)
// 이미지를 누르면 원본 크기로 확대해서 볼 수 있음
function ImageCarousel({ images, title }: { images: ProgramImage[]; title: string }) {
  const [index, setIndex] = useState(0)
  const [zoomed, setZoomed] = useState(false)
  const total = images.length

  const go = (dir: number) => setIndex(prev => (prev + dir + total) % total)

  // 확대 보기: ESC 닫기 / 좌우 방향키 이동 + 배경 스크롤 잠금
  useEffect(() => {
    if (!zoomed) return

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomed(false)
      else if (e.key === 'ArrowLeft') setIndex(prev => (prev - 1 + total) % total)
      else if (e.key === 'ArrowRight') setIndex(prev => (prev + 1) % total)
    }

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKey)

    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', handleKey)
    }
  }, [zoomed, total])

  return (
    <div>
      <button
        type="button"
        onClick={() => setZoomed(true)}
        aria-label="이미지 크게 보기"
        className="block w-full p-0 border-none bg-transparent cursor-zoom-in"
      >
        <img
          src={images[index].url}
          alt={`${title} 이미지 ${index + 1}`}
          className="w-full rounded-xl object-contain"
        />
      </button>

      {/* 컨트롤 — 이미지를 가리지 않도록 아래쪽에 배치 */}
      {total > 1 && (
        <div className="flex justify-center items-center gap-3 mt-3">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="이전 이미지"
            className="w-8 h-8 shrink-0 rounded-full bg-white hover:bg-[#f5f2f0] text-[#887267] text-[18px] font-bold leading-none border border-[#e8e0dc] cursor-pointer transition-colors flex items-center justify-center"
          >
            ‹
          </button>

          <div className="flex items-center gap-1.5">
            {images.map((img, i) => (
              <button
                key={`${img.url}-${i}`}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`${i + 1}번째 이미지 보기`}
                className={[
                  'rounded-full border-none cursor-pointer transition-all p-0',
                  i === index ? 'w-5 h-[7px] bg-[#887267]' : 'w-[7px] h-[7px] bg-[#ddd5d0] hover:bg-[#bfb2ab]',
                ].join(' ')}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => go(1)}
            aria-label="다음 이미지"
            className="w-8 h-8 shrink-0 rounded-full bg-white hover:bg-[#f5f2f0] text-[#887267] text-[18px] font-bold leading-none border border-[#e8e0dc] cursor-pointer transition-colors flex items-center justify-center"
          >
            ›
          </button>
        </div>
      )}

      {/* 확대 보기 */}
      {zoomed && (
        <div
          onClick={() => setZoomed(false)}
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
        >
          {/* 닫기 */}
          <button
            type="button"
            onClick={() => setZoomed(false)}
            aria-label="닫기"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white text-[22px] leading-none border-none cursor-pointer transition-colors flex items-center justify-center"
          >
            ×
          </button>

          <img
            src={images[index].url}
            alt={`${title} 이미지 ${index + 1}`}
            onClick={e => e.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg cursor-default"
          />

          {total > 1 && (
            <>
              <button
                type="button"
                onClick={e => { e.stopPropagation(); go(-1) }}
                aria-label="이전 이미지"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white text-[24px] font-bold leading-none border-none cursor-pointer transition-colors flex items-center justify-center"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={e => { e.stopPropagation(); go(1) }}
                aria-label="다음 이미지"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white text-[24px] font-bold leading-none border-none cursor-pointer transition-colors flex items-center justify-center"
              >
                ›
              </button>
              <span className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/15 text-white text-[13px] font-medium">
                {index + 1} / {total}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default function ProgramDetail() {
  const { program_id } = useParams<{ program_id: string }>()
  const navigate = useNavigate()
  const [program, setProgram] = useState<ProgramDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchProgram()
  }, [program_id])

  const fetchProgram = async () => {
    setLoading(true)
    try {
      const res = await fetch(getApiUrl(`/marubit/api/program/${program_id}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setProgram(data)
    } catch {
      alert('프로그램 정보를 불러오는 데 실패했습니다.')
      navigate('/program')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  }

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  if (!program) return null

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 뒤로가기 */}
        <button
          onClick={() => navigate('/program')}
          className="mb-6 text-[14px] text-gray-400 hover:text-[#887267] transition-colors flex items-center gap-1"
        >
          ← 목록으로
        </button>

        <div className="flex flex-col lg:flex-row gap-6">

          {/* 왼쪽 - 이미지 */}
          <div className="lg:w-[340px] shrink-0">
            <div className="bg-white rounded-2xl p-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)] sticky top-6">
              {program.images.length > 0 ? (
                <ImageCarousel images={program.images} title={program.title ?? '프로그램'} />
              ) : (
                <div className="w-full aspect-[3/4] bg-gray-100 rounded-xl flex items-center justify-center text-gray-300 text-[13px]">
                  이미지 없음
                </div>
              )}
            </div>
          </div>

          {/* 오른쪽 - 정보 */}
          <div className="flex-1">

            {/* 제목 */}
            <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
              <div className="flex items-start justify-between gap-3 mb-4">
                <h1 className="text-[22px] font-extrabold text-[#222]">{program.title}</h1>
                {program.is_closed === 1 && (
                  <span className="shrink-0 px-3 py-1 text-[12px] font-semibold bg-gray-100 text-gray-400 rounded-full">마감</span>
                )}
              </div>

              <div className="space-y-2 text-[14px]">
                {program.target && (
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-[80px] shrink-0">대상</span>
                    <span className="text-[#222]">{program.target}</span>
                  </div>
                )}
                {program.location && (
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-[80px] shrink-0">장소</span>
                    <span className="text-[#222]">{program.location}</span>
                  </div>
                )}
                {!program.has_sessions && program.run_start && program.run_end && (
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-[80px] shrink-0">운영기간</span>
                    <span className="text-[#222]">{formatDate(program.run_start)} ~ {formatDate(program.run_end)}</span>
                  </div>
                )}
                {program.schedule_text && (
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-[80px] shrink-0">운영일정</span>
                    <span className="text-[#222]">{program.schedule_text}</span>
                  </div>
                )}
                {program.accepts_online_application === 1 && (
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-[80px] shrink-0">신청기간</span>
                    <span className="text-[#222]">
                      {program.apply_start && program.apply_end
                        ? `${formatDate(program.apply_start)} ~ ${formatDate(program.apply_end)}`
                        : '상시 신청'}
                    </span>
                  </div>
                )}
                {program.capacity > 0 && (
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-[80px] shrink-0">모집인원</span>
                    <span className="text-[#222]">
                      {program.capacity}명
                      {program.accepts_online_application === 1 && ` (${program.applied_count}명 신청)`}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* 회차 */}
            {program.has_sessions && program.sessions.length > 0 && (
              <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
                  회차 정보
                </h2>
                <div className="space-y-2">
                  {program.sessions.map(session => (
                    <div key={session.session_id} className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-[10px] text-[13px]">
                      <span className="font-semibold text-[#222]">{session.session_name ?? `${session.order_num + 1}회차`}</span>
                      <span className="text-gray-400">{formatDateTime(session.run_start)} ~ {formatDateTime(session.run_end)}</span>
                      {session.capacity > 0 && (
                        <span className="text-gray-400">{session.capacity}명</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 내용 */}
            {program.description && (
              <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
                  프로그램 소개
                </h2>
                <p className="text-[14px] text-[#333] leading-relaxed whitespace-pre-line">{program.description}</p>
              </div>
            )}

            {/* 신청 버튼 — 온라인 신청을 받지 않는 프로그램은 안내 문구만 노출 */}
            {program.accepts_online_application === 0 ? (
              <div className="w-full rounded-xl bg-[#fdf8f0] border border-[#e8d8ce] px-6 py-5 text-center">
                <p className="m-0 text-[15px] font-bold text-[#5a4035]">온라인 신청을 받지 않는 프로그램입니다.</p>
                <p className="m-0 mt-1 text-[13px] text-[#8a7568]">전화 또는 문자로 문의해주세요.</p>
              </div>
            ) : (
              <button
                onClick={() => navigate(`/program/${program_id}/apply`, {
                  state: { application_setting: program.application_setting }
                })}
                disabled={program.is_closed === 1}
                className={[
                  'w-full h-[52px] border-none rounded-xl text-[16px] font-bold transition-colors shadow-[0_4px_16px_rgba(136,114,103,0.25)]',
                  program.is_closed === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-[#887267] text-white cursor-pointer hover:bg-[#6b5248]',
                ].join(' ')}
              >
                {program.is_closed === 1 ? '신청 마감' : '신청하기'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}